import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout, { PageHeader } from "@/components/Layout";
import client from "@/lib/api";
import { PhoneOutgoing, Search, Loader2 } from "lucide-react";

// Supervisor-facing searchable audit of every 8x8 click-to-dial call,
// tied to the dispatcher who placed it and the incident it belongs to.
export default function CallAudit() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await client.get("/dial/audit", { params: { q, limit: 200 } });
        setRows(data.calls);
        setTotal(data.total);
      } catch { /* role-gated */ }
      setLoading(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  return (
    <Layout>
      <div className="p-4 md:p-6" data-testid="call-audit-page">
        <PageHeader icon={PhoneOutgoing} title="8x8 CALL AUDIT" subtitle={`${total} outbound calls logged · click-to-dial via 8x8 Work`} />
        <div className="flex items-center gap-2 bg-[#141824] border border-[#232838] px-3 py-2 mt-4 w-full max-w-md focus-within:border-[#3B82F6]">
          {loading ? <Loader2 size={14} className="text-[#3B82F6] animate-spin" /> : <Search size={14} className="text-[#6B7280]" />}
          <input data-testid="call-audit-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search dispatcher, phone, agency, CC number…"
            className="flex-1 bg-transparent outline-none text-sm font-mono text-white placeholder:text-[#6B7280]" />
        </div>
        <div className="mt-4 border border-[#232838] rounded-lg overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="call-audit-table">
            <thead className="bg-[#141824] font-mono text-[10px] uppercase text-[#8299bd]">
              <tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-2">Dispatcher</th>
                <th className="px-2">Unit</th>
                <th className="px-2">Called</th>
                <th className="px-2">Number</th>
                <th className="px-2">Incident</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1a2130]">
              {rows.map((r, i) => (
                <tr key={i} className="hover:bg-[#10141f]" data-testid={`call-audit-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-[#9CA3AF] whitespace-nowrap">
                    {new Date(r.ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
                  </td>
                  <td className="px-2 text-white">{r.by}<span className="font-mono text-[10px] text-[#5f7396] ml-1.5">{(r.by_role || "").replace(/_/g, " ")}</span></td>
                  <td className="px-2 font-mono text-[11px] text-[#8299bd]">{r.badge || "—"}</td>
                  <td className="px-2 text-[#E5E7EB]">{r.target_name || "—"}</td>
                  <td className="px-2 font-mono text-[#4ade80]">{r.phone}</td>
                  <td className="px-2">
                    {r.incident_cc ? (
                      <button data-testid={`call-audit-cc-${r.incident_cc}`} onClick={() => nav(`/dispatch?cc=${r.incident_cc}`)}
                        className="font-mono text-[11px] text-[#22D3EE] hover:underline">CC {r.incident_cc}</button>
                    ) : <span className="font-mono text-[10px] text-[#5f7396]">directory dial</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && rows.length === 0 && (
            <div className="p-4 font-mono text-sm text-[#8299bd]">No calls match{q ? ` “${q}”` : ""}.</div>
          )}
        </div>
        <div className="font-mono text-[10px] text-[#5B6472] mt-3">
          Every 8x8 click-to-dial is logged with dispatcher, destination and incident linkage. Calls placed from an incident's transfer banner are also stamped into that incident's event log.
        </div>
      </div>
    </Layout>
  );
}
